/**
 * Wrapper autour du module WebAssembly `mp_wasm`.
 *
 * Charge le binaire, instancie `WasmEngine`, et expose deux fonctions
 * de commodité (`snapshot`, `advanceTurn`) qui parsent les JSON retournés
 * en valeurs typées.
 */

import init, {
  WasmEngine,
  embeddedDecisionsJson,
  embeddedEuropeanInterventionsJson,
} from "../wasm-pkg/mp_wasm";
// `?url` demande à Vite de fournir l'URL finale du .wasm en production —
// sinon l'import relatif de `new URL(...)` dans le glue JS peut casser.
import wasmUrl from "../wasm-pkg/mp_wasm_bg.wasm?url";

import type { TurnResult, WorldState } from "./types";

let initialized = false;

export async function loadWasm(): Promise<void> {
  if (initialized) return;
  await init(wasmUrl);
  initialized = true;
}

export async function getEmbeddedDecisionsJson(): Promise<string> {
  await loadWasm();
  return embeddedDecisionsJson();
}

export async function getEmbeddedEuropeanInterventionsJson(): Promise<string> {
  await loadWasm();
  return embeddedEuropeanInterventionsJson();
}

export interface EngineBootstrap {
  archetypeJson: string;
  planJson: string;
  decisionsJson: string;
  seed?: bigint;
}

export async function createEngine(cfg: EngineBootstrap): Promise<WasmEngine> {
  await loadWasm();
  const seed = cfg.seed ?? 42n;
  return new WasmEngine(
    cfg.archetypeJson,
    cfg.planJson,
    cfg.decisionsJson,
    seed,
  );
}

/**
 * Parse défensif des JSON produits par le moteur. Un panic WASM ou un
 * JSON corrompu remontait en exception brute → écran blanc React sans
 * message (audit robustesse 2026-06-10). On contextualise l'erreur pour
 * que l'UI puisse l'afficher et proposer une récupération — la save
 * serveur persiste, un rechargement rejoue la partie.
 */
function parseEngineJson<T>(raw: string, what: string): T {
  try {
    return JSON.parse(raw) as T;
  } catch (e) {
    throw new Error(
      `Réponse moteur illisible (${what}) : ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}

export function snapshot(engine: WasmEngine): WorldState {
  const raw = engine.currentStateJson();
  return parseEngineJson<WorldState>(raw, "état du monde");
}

export function advanceTurn(
  engine: WasmEngine,
  choices: string[] = [],
): TurnResult {
  let raw: string;
  try {
    raw = engine.applyTurn(JSON.stringify(choices));
  } catch (e) {
    // Panic WASM ou choix rejeté par le moteur (id inconnu, etc.).
    throw new Error(
      `Le moteur a refusé le tour : ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  return parseEngineJson<TurnResult>(raw, "résultat du tour");
}

/** Prochain archétype suggéré par le moteur (selector scripté ou émergent). */
export function suggestedNext(engine: WasmEngine): string | null {
  return engine.suggestedNext() ?? null;
}

export type SelectorMode = "scripted" | "emergent";

export function selectorMode(engine: WasmEngine): SelectorMode {
  return engine.selectorMode() as SelectorMode;
}

/** « Agir » — injecte une décision de redressement réservée à un indicateur.
 *  Retourne `true` si le moteur l'a bien posée comme prochaine question. */
export function injectRemediation(
  engine: WasmEngine,
  decisionId: string,
): boolean {
  return engine.injectRemediation(decisionId);
}

/** « Agir » — annule le redressement en cours (restaure la décision normale). */
export function cancelRemediation(engine: WasmEngine): void {
  engine.cancelRemediation();
}

/** La Bascule — id de l'opportunité exceptionnelle proposée, ou null. */
export function availableOpportunity(engine: WasmEngine): string | null {
  return engine.availableOpportunity() ?? null;
}

/** La Bascule — saisit l'opportunité (injecte la décision). `false` si l'id ne
 *  correspond pas à l'opportunité réellement proposée. */
export function seizeOpportunity(
  engine: WasmEngine,
  decisionId: string,
): boolean {
  return engine.seizeOpportunity(decisionId);
}

/** Contrat de campagne — promesses assumées + progrès normalisé
 *  (1.0 = tenue, −1.0 = trahie). */
export interface PledgeView {
  id: string;
  label: string;
  chosen_at_turn: number;
  progress: number;
}

export function pledges(engine: WasmEngine): PledgeView[] {
  return parseEngineJson<PledgeView[]>(engine.pledgesJson(), "promesses");
}

/** Ids des décisions one-shot déjà jouées (pour la réserve de redressements). */
export function playedDecisions(engine: WasmEngine): string[] {
  return parseEngineJson<string[]>(
    engine.playedDecisionsJson(),
    "décisions jouées",
  );
}

/** Vue UI d'un effet différé en attente. Mirror de `PendingEffectView` côté Rust. */
export interface PendingEffect {
  target_turn: number;
  option_label: string;
  archetype_kicker: string;
  /** Prompt original de la décision — lève l'ambiguïté de option_label
   *  (ex: « Supprimer progressivement » ne dit pas QUOI). Optionnel
   *  pour compat avec les saves antérieures au 2026-05-27. */
  archetype_prompt?: string;
}

export function pendingEffects(engine: WasmEngine): PendingEffect[] {
  const raw = engine.pendingEffectsJson();
  return parseEngineJson<PendingEffect[]>(raw, "effets différés");
}
